/**
 * Suno Pronunciation Fix Dictionary
 *
 * Maps commonly mispronounced words to phonetic alternatives that Suno handles better.
 * Add new entries as you discover more pronunciation issues.
 *
 * Custom fixes are persisted to DB via API.
 * localStorage is used as a read-only fallback for initial load / offline sync scanning.
 */

import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const STORAGE_KEY = 'custom_pronunciation_fixes';
const API_BASE = '/api/pronunciation-fixes';

export interface PronunciationFix {
  original: string;
  replacement: string;
  reason: string;
}

export const PRONUNCIATION_DICTIONARY: PronunciationFix[] = [
  // Common problem words
  { original: "bury", replacement: "berry", reason: "Suno pronounces 'bury' as 'burr-ee' instead of 'berry'" },
  { original: "buried", replacement: "berried", reason: "Suno mispronounces 'buried' - use 'berried' for correct sound" },
  { original: "hour", replacement: "our", reason: "Suno often adds an 'h' sound - 'our' works better" },
  { original: "hours", replacement: "ours", reason: "Suno often adds an 'h' sound - 'ours' works better" },
  { original: "herb", replacement: "erb", reason: "Suno pronounces the 'h' - use 'erb' for silent h" },
  { original: "herbs", replacement: "erbs", reason: "Suno pronounces the 'h' - use 'erbs' for silent h" },

  // Pronunciation emphasis
  { original: " tear ", replacement: " teer ", reason: "Disambiguates 'tear' (cry) vs 'tear' (rip)" },
  { original: " tears ", replacement: " teers ", reason: "Disambiguates 'tears' (crying) vs 'tears' (ripping)" },
  { original: "read ", replacement: "reed ", reason: "Forces present tense pronunciation of 'read'" },
  { original: " lead ", replacement: " leed ", reason: "Disambiguates 'lead' (verb) vs 'lead' (metal)" },

  // Contractions that Suno struggles with
  { original: "won't", replacement: "wont", reason: "Suno sometimes mispronounces contractions" },
  { original: "can't", replacement: "cant", reason: "Clearer pronunciation without apostrophe" },

  // Add more as discovered...
];

/**
 * Get custom pronunciation fixes from API, falling back to localStorage.
 * Always writes the result back to localStorage so getAllFixesSync() stays current.
 */
export async function getCustomFixes(): Promise<PronunciationFix[]> {
  try {
    const res = await fetchWithCsrf(`${API_BASE}/list`);
    if (res.ok) {
      const json = await res.json();
      if (json.data) {
        const fixes: PronunciationFix[] = json.data.map((f: any) => ({
          original: f.original,
          replacement: f.replacement,
          reason: f.reason,
        }));
        // Keep localStorage in sync so the sync scanner (getAllFixesSync) can find them
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fixes)); } catch { /* quota */ }
        return fixes;
      }
    }
  } catch {
    // API unavailable — fall through to localStorage
  }
  return getCustomFixesFromStorage();
}

/**
 * Get custom fixes from localStorage only (synchronous fallback)
 */
export function getCustomFixesFromStorage(): PronunciationFix[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save custom pronunciation fix to API, then refresh the localStorage cache.
 */
export async function addCustomFix(fix: PronunciationFix): Promise<void> {
  await fetchWithCsrf(`${API_BASE}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fix),
  });
  // Refresh cache so the sync scanner picks up the new fix immediately
  await getCustomFixes();
}

/**
 * Remove custom pronunciation fix from API, then refresh the localStorage cache.
 */
export async function removeCustomFix(original: string): Promise<void> {
  await fetchWithCsrf(`${API_BASE}/${encodeURIComponent(original)}`, {
    method: 'DELETE',
  });
  // Refresh cache so the sync scanner drops the removed fix immediately
  await getCustomFixes();
}

/**
 * Get combined dictionary (default + custom) — async version
 */
export async function getAllFixes(): Promise<PronunciationFix[]> {
  const custom = await getCustomFixes();
  return [...PRONUNCIATION_DICTIONARY, ...custom];
}

/**
 * Get combined dictionary (default + custom) — sync version for scanning
 */
export function getAllFixesSync(): PronunciationFix[] {
  return [...PRONUNCIATION_DICTIONARY, ...getCustomFixesFromStorage()];
}

export interface ReplacementResult {
  original: string;
  fixed: string;
  changes: Array<{
    word: string;
    replacement: string;
    reason: string;
    lineNumber: number;
  }>;
}

/**
 * Scan lyrics for problem words and return suggested replacements
 */
export function scanForPronunciationIssues(lyrics: string): ReplacementResult {
  let fixedLyrics = lyrics;
  const changes: ReplacementResult['changes'] = [];

  const lines = lyrics.split('\n');
  const allFixes = getAllFixesSync();

  lines.forEach((line, lineIndex) => {
    allFixes.forEach(fix => {
      // Case-insensitive search, but preserve original case in replacement
      const regex = new RegExp(fix.original, 'gi');

      if (regex.test(line)) {
        // Track the change
        const matches = line.match(regex);
        if (matches) {
          matches.forEach(() => {
            changes.push({
              word: fix.original,
              replacement: fix.replacement,
              reason: fix.reason,
              lineNumber: lineIndex + 1
            });
          });
        }

        // Apply the replacement (case-preserving)
        fixedLyrics = fixedLyrics.replace(regex, (match) => {
          // Preserve capitalization pattern
          if (match[0] === match[0].toUpperCase()) {
            return fix.replacement.charAt(0).toUpperCase() + fix.replacement.slice(1);
          }
          return fix.replacement;
        });
      }
    });
  });

  return {
    original: lyrics,
    fixed: fixedLyrics,
    changes
  };
}

/**
 * Apply pronunciation fixes to lyrics
 */
export function applyPronunciationFixes(lyrics: string): string {
  const result = scanForPronunciationIssues(lyrics);
  return result.fixed;
}
